import { DeleteIcon, EditAltIcon, RmxCheckIcon } from 'cokitchen-icons';
import React, { useMemo } from 'react';
import { Table, TableData, TableRow } from '@cokitchen/cokitchen-components';
import SelectDropdown from 'components/base/select-dropdown';
import EditableInput from 'components/base/editable-input';
import useVendorContext from 'contexts/vendor.context';
import useDashboardContext from 'contexts/dashboard.context';
import EmptyStoreItemRow from './empty-store-item-row';

type Ingredient = {
  id: string;
  name: string;
  unit_type: string;
};

type StoreItemsTabProps = {
  editingStoreItemKey: string | null;
  onStoreItemInputChange: (
    vendorIndex: number,
    itemIndex: number,
    field: string,
    value: string
  ) => void;
  onEditStoreItem: (vendorIndex: number, itemIndex: number) => void;
  onSaveStoreItem: () => void;
  onDeleteStoreItem: (vendorIndex: number, itemIndex: number) => void;
  onAddStoreItemToVendor: (vendorIndex: number, ingredientId: string) => void;
};

const STORE_ITEMS_HEADERS = ['Vendor Name', 'Item Name', 'Price', 'Actions'];

const StoreItemsTab: React.FC<StoreItemsTabProps> = ({
  editingStoreItemKey,
  onStoreItemInputChange,
  onEditStoreItem,
  onSaveStoreItem,
  onDeleteStoreItem,
  onAddStoreItemToVendor
}) => {
  const { vendorFormData } = useVendorContext();
  const { formStoreItems } = useDashboardContext();

  const ingredientMap = useMemo(() => {
    return formStoreItems.reduce((acc, ingredient) => {
      acc[ingredient.id] = ingredient;
      return acc;
    }, {} as Record<string, Ingredient>);
  }, [formStoreItems]);

  const getItemKey = (vendorIndex: number, itemIndex: number): string => {
    return `${vendorIndex}-${itemIndex}`;
  };

  const isItemEditing = (vendorIndex: number, itemIndex: number): boolean => {
    return editingStoreItemKey === getItemKey(vendorIndex, itemIndex);
  };

  const isItemEmpty = (ingredientId: string, gramPrice: string): boolean => {
    return !ingredientId && !gramPrice;
  };

  const getIngredientName = (id: string): string => {
    return ingredientMap[id]?.name || id || '';
  };

  const getIngredientUnitType = (id: string): string => {
    return ingredientMap[id]?.unit_type || '';
  };

  return (
    <Table headers={STORE_ITEMS_HEADERS}>
      {vendorFormData.flatMap((storeItem, vendorIndex) => {
        if (!storeItem.store_items || storeItem.store_items.length === 0) {
          return [
            <EmptyStoreItemRow
              key={`${vendorIndex}-0`}
              vendorName={storeItem.name}
              vendorIndex={vendorIndex}
              onAddStoreItemToVendor={onAddStoreItemToVendor}
            />
          ];
        }

        return storeItem.store_items.map((item, itemIndex) => {
          return (
            <TableRow
              key={getItemKey(vendorIndex, itemIndex)}
              className='hover:bg-gray-50 transition-colors'>
              <TableData className='px-2 py-2 text-sm text-gray-800'>{storeItem.name}</TableData>

              <TableData className='px-2 py-2 text-sm text-gray-800'>
                <div className={`${item.ingredient_id ? '' : 'border border-red-5'}`}>
                  {isItemEditing(vendorIndex, itemIndex) ? (
                    <SelectDropdown
                      placeholder='Select ingredient'
                      value={getIngredientName(item.ingredient_id)}
                      setValue={(ing) =>
                        onStoreItemInputChange(vendorIndex, itemIndex, 'ingredient_id', ing.id)
                      }
                      options={formStoreItems}
                    />
                  ) : (
                    <span>{getIngredientName(item.ingredient_id)}</span>
                  )}
                </div>
              </TableData>

              <TableData className='px-2 py-2 text-sm text-gray-800'>
                {isItemEditing(vendorIndex, itemIndex) ||
                isItemEmpty(item.ingredient_id, item.gram_price) ? (
                  <EditableInput
                    isEditing
                    showError
                    type='number'
                    value={item.gram_price}
                    placeholder='Enter price'
                    onChange={(e) =>
                      onStoreItemInputChange(vendorIndex, itemIndex, 'gram_price', e.target.value)
                    }
                  />
                ) : (
                  <p
                    className={`${
                      item.gram_price ? '' : 'border border-red-5 w-full max-w-48 p-2'
                    }`}>
                    ₦ {Number(item.gram_price).toLocaleString()}
                    {getIngredientUnitType(item.ingredient_id) &&
                      `/${getIngredientUnitType(item.ingredient_id)}`}
                  </p>
                )}
              </TableData>

              <TableData className='px-2 py-2 text-sm'>
                <div className='flex items-center gap-3'>
                  {isItemEditing(vendorIndex, itemIndex) ? (
                    <div className='cursor-pointer text-green-6' onClick={onSaveStoreItem}>
                      <RmxCheckIcon />
                    </div>
                  ) : (
                    <div
                      className='cursor-pointer text-blue-5'
                      onClick={() => onEditStoreItem(vendorIndex, itemIndex)}>
                      <EditAltIcon />
                    </div>
                  )}
                  <div
                    className='cursor-pointer text-red-5'
                    onClick={() => onDeleteStoreItem(vendorIndex, itemIndex)}>
                    <DeleteIcon />
                  </div>
                </div>
              </TableData>
            </TableRow>
          );
        });
      })}
    </Table>
  );
};

export default StoreItemsTab;
